import '.du{display:none;}.ssr-plugin'
import React, { ReactNode, useEffect, useState } from 'react'
import { useToast } from '../Toast/index.js'
import { useClient } from '@based/sdk/react'
import { createRoot } from 'react-dom/client'
import { prettyNumber } from '@based/pretty-number'
import { InlineFilePreviewItem } from './InlineFilePreviewItem.js'
import { styled } from 'inlines'

type InlineFileInputProps = {
  children?: ReactNode
  stream?: string
  multiple?: boolean
  accept?: string
  limit?: number //in bytes
  onChange?: (file: File, uploadedUrl: string) => void
  payload?: any
}

export const InlineFileInput = ({
  children,
  stream = 'file-upload',
  multiple = true,
  accept,
  limit,
  onChange,
  payload,
}: InlineFileInputProps) => {
  const toast = useToast()
  const client = useClient()
  const ref = React.useRef<HTMLInputElement>()
  const toastRef = React.useRef<HTMLDivElement>()
  const rootRef = React.useRef<any>(null) // Store root instance in ref

  const abortControllersRef = React.useRef<{ [key: string]: AbortController }>(
    {},
  )

  const [uploadProgress, setUploadProgress] = useState<{
    [key: string]: number
  }>({})

  const cancelUpload = (fileName: string) => {
    // Abort the upload
    if (abortControllersRef.current[fileName]) {
      abortControllersRef.current[fileName].abort()
      delete abortControllersRef.current[fileName]
    }

    // Remove from progress
    setUploadProgress((prev) => {
      const next = { ...prev }
      delete next[fileName]
      return next
    })
  }

  let uploadFilesLength = Object.values(uploadProgress).length

  useEffect(() => {
    if (toastRef.current) {
      if (!rootRef.current) {
        rootRef.current = createRoot(toastRef.current.children[1])
      }

      toastRef.current.getElementsByTagName('div')[2].style.fontFamily =
        'Geist, Inter'
      toastRef.current.getElementsByTagName('div')[2].style.fontSize = '14px'
      toastRef.current.getElementsByTagName('div')[2].innerText =
        `${Object.values(uploadProgress).filter((v) => v === 100).length} of ${uploadFilesLength} file${uploadFilesLength > 1 ? 's' : ''} uploaded`

      rootRef.current.render(
        <>
          {Object.entries(uploadProgress).map(([fileName, progress], idx) => (
            <InlineFilePreviewItem
              fileName={fileName}
              progress={progress}
              cancelUpload={cancelUpload}
              key={idx}
            />
          ))}
        </>,
      )
    }
  }, [toastRef.current, uploadProgress])

  return (
    <styled.div
      onClick={() => {
        ref.current.click()
      }}
    >
      {children}
      <input className="du"
        ref={ref}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={async (e) => {
          if (!e.target.files.length) return
          const files = [...e.target.files]

          if (limit) {
            let fileSizes = 0
            for (let i = 0; i < files.length; i++) {
              fileSizes += files[i].size
            }
            if (fileSizes > limit) {
              toast('Files are too big', {
                description: `Please upload files smaller than ${prettyNumber(limit, 'number-bytes')}`,
                icon: 'error',
                color: 'red',
              })
              return
            }
          }

          setUploadProgress(
            files.reduce((acc, file) => ({ ...acc, [file.name]: 0 }), {}),
          )

          const id = toast(
            `Uploading ${files.length} file${files.length > 1 ? 's' : ''}... `,
            {
              description: 'Time left',
              icon: 'ended-arrow-up',
              secondaryButtonLabel: 'Dismiss',
              onSecondaryClick: () => {
                // !TODO: Cancel upload
                // console.log('clicked the cancel button on toast 🐸')
              },
              expandedContent: <></>,
              permanent: false,
              ref: toastRef,
            },
          )

          await Promise.allSettled(
            files.map(async (file) => {
              try {
                const controller = new AbortController()
                abortControllersRef.current[file.name] = controller

                const res = await client.stream(
                  stream,
                  {
                    contents: file,
                    fileName: file.name,
                    mimeType: file.type,
                    payload,
                  },
                  (progress) => {
                    setUploadProgress((prev) => ({
                      ...prev,
                      [file.name]: Math.round(progress * 100),
                    }))
                  },
                  // { signal: controller.signal }, // Pass abort signal to stream ???
                )

                // Cleanup controller after success
                delete abortControllersRef.current[file.name]

                setUploadProgress((prev) => ({
                  ...prev,
                  [file.name]: 100,
                }))

                // Call onChange with the uploaded URL for each file
                if (onChange && res) {
                  const uploadedUrl =
                    typeof res === 'string' ? res : res.url || res.src || res
                  onChange(file, uploadedUrl)
                }
              } catch (error) {
                console.error(`❌ Failed to upload ${file.name}: ${error}`)
              }
            }),
          ).then(() => {
            if (rootRef.current) {
              // show chekmark + success message
              toastRef.current.getElementsByTagName(
                'svg',
              )[0].style.marginBottom = '-6px'
              toastRef.current.getElementsByTagName('svg')[0].innerHTML =
                `<path fill-rule="evenodd" clip-rule="evenodd" d="M9 17.5C13.6944 17.5 17.5 13.6944 17.5 9C17.5 4.30558 13.6944 0.5 9 0.5C4.30558 0.5 0.5 4.30558 0.5 9C0.5 13.6944 4.30558 17.5 9 17.5ZM12.8927 5.89984L7.35356 11.458L5.10731 9.19273C5.01381 9.09907 4.89547 9.05223 4.75231 9.05223C4.60897 9.05223 4.49097 9.09907 4.39831 9.19273C4.30564 9.28623 4.25931 9.4044 4.25931 9.54723C4.25931 9.69023 4.30614 9.8079 4.39981 9.90023L6.78806 12.2695C6.94972 12.4311 7.13822 12.512 7.35356 12.512C7.56889 12.512 7.75739 12.4311 7.91906 12.2695L13.6002 6.60734C13.6939 6.51384 13.7407 6.39551 13.7407 6.25234C13.7407 6.10901 13.6939 5.991 13.6002 5.89834C13.5067 5.80567 13.3885 5.75934 13.2457 5.75934C13.1027 5.75934 12.985 5.80617 12.8927 5.89984Z" fill="#10B981" fill-opacity="0.8" style="fill:#10B981;fill:color(display-p3 0.0627 0.7255 0.5059);fill-opacity:0.8;"/>`
              toastRef.current.getElementsByTagName('div')[2].style.fontFamily =
                'Geist, Inter'
              toastRef.current.getElementsByTagName('div')[2].style.fontSize =
                '14px'
              toastRef.current.getElementsByTagName('div')[2].innerText =
                `${files.length} file${files.length > 1 ? 's' : ''} uploaded successfully`
              toastRef.current.getElementsByTagName('div')[4].style.display =
                'none'
              // hide the cancel button and the expand button
              if (toastRef.current.getElementsByTagName('button')[0]) {
                toastRef.current.getElementsByTagName(
                  'button',
                )[0].style.display = 'none'
              }
              if (toastRef.current.getElementsByTagName('button')[1]) {
                toastRef.current.getElementsByTagName(
                  'button',
                )[1].style.display = 'none'
              }

              // target the closing/ dismiss button
              const dismissDiv = toastRef.current.getElementsByTagName('div')[5]
                .childNodes[2] as HTMLElement
              if (dismissDiv) {
                dismissDiv.style.minWidth = '36px'
                dismissDiv.style.minHeight = '36px'
              }

              // hide bottom part
              if (toastRef.current.getElementsByTagName('div')[12]) {
                toastRef.current.getElementsByTagName('div')[12].style.display =
                  'none'
              }
            }
            setTimeout(() => {
              toast.dismiss(id)
            }, 5000)
          })
        }}
      />
    </styled.div>
  )
}
